/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { MusicKey, TunePadAudio, toInt } from '../audio';
import { TunePadCell } from './cell';
import { TunePadFile } from './file';
import { PythonRuntime } from '../compiler/runtime';
import { ProjectPersistence } from './persistence';
import { TunePadSample } from './sample';

/**
 * ## TunePad project
 * ```html
 * <tunepad-project
 *    uuid = "{{ database UUID }}"
 *    name = "{{ python module name }}"  // must be a valid python identifier
 *    readonly = "false"                 // default is false
 *    tempo = "90"
 *    key = "C Major"
 *    persistence = "wss://d65.tunepad.space/websocket/"
 *    awareness = "{username}:{user-color}" or "{username}"
 *    time = "4/4">
 * 
 *    <!-- optional -->
 *    <tunepad-project-menu>
 * 
 *    <!-- optional -->
 *    <tunepad-block-menu> ... </tunepad-block-menu>
 * 
 *    <!-- required -->
 *    <tunepad-cell-list>
 *        <tunepad-cell></tunepad-cell>
 *        <tunepad-cell></tunepad-cell>
 *        <p> Other html content </p>
 *        <tunepad-cell></tunepad-cell>
 * 
 *        <!-- etc -->
 *    </tunepad-cell-list>
 * 
 *    <!-- optional -->
 *    <tunepad-outline></tunepad-outline>
 * </tunepad-project>
 * ```
 */
export class TunePadProject extends HTMLElement {

    static readonly ELEMENT = "tunepad-project";

    static observedAttributes = [
        "uuid",        // database ID (should never change)
        "name",        // name of this project
        "readonly",    // boolean string, default is false
        "tempo",
        "key",
        "share",       // "private", "viewonly", "collaborative"
        "time",        // time signature (e.g. "4/4")
        "voices",      // path to patch list for synth (e.g. /assets/sounds/voices)
        "persistence", // Y.js websocket persistence provider (e.g. "ws://localhost:3003")
        "awareness"    // Y.js awareness "{username}:{user-color}" or "{username}"
    ];

    /** unique database identifier */
    uuid : string = self.crypto.randomUUID();

    /** project name */
    name : string = 'tunepad_project';

    /** TunePad audio system */
    private audio : TunePadAudio;

    /** allows project to keep track of added and deleted tunepad-cells */
    private observer? : MutationObserver;

    constructor() {
        super();
        this.audio = TunePadAudio.init();
        this.audio.addSubscriber(this);
    }

    onClockReset() { }

    onClockTimeChange() { }

    onTempoChange() {
        this.setAttribute('tempo', `${this.audio.bpm}`);
    }

    onKeyChange() {
        this.setAttribute('key', this.audio.key.name);
    }

    onTimeSignatureChange() { 
        this.setAttribute('time', `${this.audio.beatsPerMeasure}/${this.audio.beatValue}`);
    }

    async connectedCallback() {
        const proj = this;

        /**
         * Generate custom events for cells added, removed, or updated
         */
        this.observer = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeName.toLowerCase() === 'tunepad-cell') {
                        const cell = node as TunePadCell;
                        cellUpdateEvent(new CustomEvent('cell-added', { 
                            bubbles: false, composed: true, detail : { uuid : cell.uuid }
                        }));
                        cell.addEventListener('update-cell', cellUpdateEvent);
                        cell.addEventListener('delete-cell', cellUpdateEvent);
                        cell.addEventListener('copy-cell', relayEvent);
                        cell.addEventListener('compiled', relayEvent);
                        cell.addEventListener('cell-log', relayEvent);
                    }
                    else if (node.nodeName.toLocaleLowerCase() === 'tunepad-sample') {
                        const sample = node as TunePadSample;
                        cellUpdateEvent(new CustomEvent('cell-added', { 
                            bubbles: false, composed: true, detail : { uuid : sample.uuid }
                        }));
                        sample.addEventListener('update-sample', cellUpdateEvent);
                        sample.addEventListener('delete-sample', cellUpdateEvent);
                    }
                }
                for (const node of mutation.removedNodes) {
                    if (node.nodeName.toLowerCase() === 'tunepad-cell') {
                        const cell = node as TunePadCell;
                        cellUpdateEvent(new CustomEvent('cell-removed', { 
                            bubbles: false, composed: true, detail : { uuid : cell.uuid }
                        }));
                        cell.removeEventListener('update-cell', cellUpdateEvent);
                        cell.removeEventListener('delete-cell', cellUpdateEvent);
                        cell.removeEventListener('copy-cell', relayEvent);
                        cell.removeEventListener('compiled', relayEvent);
                        cell.removeEventListener('cell-log', relayEvent);
                    }
                    else if (node.nodeName.toLocaleLowerCase() === 'tunepad-sample') {
                        const sample = node as TunePadSample;
                        cellUpdateEvent(new CustomEvent('cell-removed', { 
                            bubbles: false, composed: true, detail : { uuid : sample.uuid }
                        }));
                        sample.removeEventListener('update-sample', cellUpdateEvent);
                        sample.removeEventListener('delete-sample', cellUpdateEvent);
                    }
                }
            }
        });
        //this.observer?.observe(this, { subtree: true, childList: true, attributes: true, attributeOldValue: true });
        this.observer?.observe(this, { subtree: true, childList: true });


        await this.waitForWindowLoad();

        // start yjs persistence?
        if (this.hasAttribute('persistence')) {
            ProjectPersistence.Initialize(this);
        }

        this.querySelector('tunepad-project-menu')?.addEventListener('update', e => {
            const { property, value } = (e as CustomEvent).detail;
            if (property === 'key') {
                this.setAttribute('key', value);
            } 
            else if (property === 'tempo') {
                this.setAttribute('tempo', value);
            }
            else if (property === 'time') {
                this.setAttribute('time', value);
            }
            else if (property === 'name') {
                this.setAttribute('name', value);
            }
            this.emitLogEvent('menu-property', { target : property, value });
        });

        this.querySelector('tunepad-project-menu')?.addEventListener('action', e => {
            const { action } = (e as CustomEvent).detail;
            if (action === 'play') {
                this.querySelectorAll('tunepad-cell').forEach((cell) => {(cell as TunePadCell).playAudio() });
            }
            else if (action === 'pause') {
                this.querySelectorAll('tunepad-cell').forEach((cell) => {(cell as TunePadCell).pauseAudio() });
            }
            else if (action === 'stop') {
                this.audio.stopAll();
            }
            else {
                relayEvent(e as CustomEvent);
            }
            this.emitLogEvent('menu-button', { action });
        });

        this.querySelector('tunepad-outline')?.addEventListener('add-cell', e => {
            this.dispatchEvent(new CustomEvent("add-cell", {
                bubbles: false,
                composed: true,
                detail: {
                    uuid : this.uuid,
                    instrument: (e as CustomEvent).detail.instrument
                }
            }));
            this.emitLogEvent('outline-add');
        });

        this.querySelector('tunepad-outline')?.addEventListener('reorder-cells', e => {
            relayEvent(e as CustomEvent);
            this.emitLogEvent('outline-reorder');
        });


        function cellUpdateEvent(evt : Event) {
            proj.dispatchEvent(new CustomEvent('update-project', {
                bubbles: false, composed: true, detail: { uuid : proj.uuid }
            }));
        }

        function relayEvent(evt : Event) {
            proj.dispatchEvent(new CustomEvent(evt.type, {
                bubbles: false, composed: true, detail: (evt as CustomEvent).detail 
            }));
        }
    }

    disconnectedCallback() {
        this.observer?.disconnect();
    }

    attributeChangedCallback(name : string, oldValue : string | null, newValue : string | null) {
        if (newValue === oldValue) {
            return;
        }
        else if (name === 'uuid' && newValue) {
            this.uuid = newValue;
        }
        else if (name === 'name' && newValue !== this.name && newValue) {
            this.name = newValue;
            this.querySelector('tunepad-project-menu')?.setAttribute('name', newValue);
        }
        else if (name === 'tempo') {
            this.audio.bpm = toInt(newValue, 90);
        }
        else if (name === 'key' && newValue) {
            this.audio.key = new MusicKey(newValue);
        }
        else if (name === 'time' || name == 'time-signature') {
            this.audio.setTimeSignature(newValue!);
        }
        else if (name === 'voices' && newValue) {
            this.audio.voices = newValue;
        }
        else if (name === 'readonly') {
            this.setReadOnly(newValue === 'true');
        }
        else if (name === 'persistence' && oldValue === null) {
            ProjectPersistence.Initialize(this);
        }
        this.emitUpdateEvent(name, newValue);
    }


    /**
     * Serialize this project and all cells. Return a JSON stringified object
     */
    public save() : any {
        const data : any = { };
        this.getAttributeNames().forEach(attrib => {
            if (attrib !== 'persistence' && attrib !== 'awareness') {
                data[attrib] = this.getAttribute(attrib)
            }
        });
        data.cells = [];

        // FIXME: Temporarily disabling file saving because it wastes a ton of space in the database
        // data.files = [];
        // const files = document.querySelectorAll('tunepad-file');
        // for (const f of files) {
        //     const file = f as TunePadFile;
        //     data.files.push(await file.save());
        // }

        document.querySelectorAll('tunepad-cell, tunepad-sample').forEach(el => {
            data.cells.push((el as any).save());
        });
        return data;
    }



    public restore(data : any, loadPython : boolean = true) : any {
        Object.entries(data).forEach(([key, value]) => {
            if (!['cells', 'files', 'persistence', 'awareness'].includes(key)) {
                this.setAttribute(key, value as string);
            }
        });

        // FIXME: Temporarily disabling file saving because it wastes a ton of space in the database
        // if (Array.isArray(data.files)) {
        //     for (const f of data.files) {
        //         const file = this.querySelector(`tunepad-file[uuid="${f.uuid}"]`);
        //         if (file) (file as TunePadFile).restore(f);
        //     }
        // }
        const cell_list : any = { };

        if (Array.isArray(data.cells)) {
            for (const o of data.cells) {
                const type = o.type ?? 'tunepad-cell';
                const uuid = o.uuid;
                if (uuid) {
                    let cell = this.querySelector(`${type}[uuid="${uuid}"]`);
                    if (cell) {
                        cell_list[uuid] = true;
                        (cell as TunePadCell).restore(o, loadPython);
                    } else {
                        cell_list[uuid] = false;
                        cell = document.createElement(type);
                        this.querySelector('tunepad-cell-list')?.append(cell!);
                        (cell as any).restore(o, loadPython);
                    }
                }
            }
        }
        return cell_list;
    }


/**
 * Move a target <tunepad-cell> element to a new location.
 * Have to create a clone of the original to do this correctly.
 * 
 * @param moved the uuid of the cell to move
 * @param newPosition index of the cell's new position
 * @returns the <tunepad-cell> that was reinserted
 */
    public reorderCells(moved : string, newPosition : number) : TunePadCell | undefined {
        const parent = this.querySelector('tunepad-cell-list');
        if (!parent || newPosition < 0) return undefined;

        function cellAtIndex(index : number) : Element | null {
            const cells = parent!.querySelectorAll('tunepad-cell, tunepad-sample');
            return (index >= 0 && index < cells.length) ? cells[index] : null;
        }

        const el = parent!.querySelector(`[uuid="${moved}"]`);

        if (el instanceof TunePadCell) {
            const cell = el as TunePadCell;
            cell.pauseAudio();
            const clone = document.createElement('tunepad-cell') as TunePadCell;
            cell?.getAttributeNames().forEach(attrib => {
                clone.setAttribute(attrib, `${cell.getAttribute(attrib)}`);
            });
            clone.replaceCode(cell.code);
            cell.remove();
            parent.insertBefore(clone, cellAtIndex(newPosition));
            this.waitForElementConnected(clone).then(ready => {
                clone.recompile(true);
            });
            return clone;
        }
        else if (el instanceof TunePadSample) {
            el.remove();
            parent.insertBefore(el, cellAtIndex(newPosition));
        }
    }


    public async recompileAll() {
        const cells = document.querySelectorAll('tunepad-cell');
        for (const cell of cells) {
            await PythonRuntime.preload((cell as TunePadCell));
        }
        for (const cell of cells) {
            await (cell as TunePadCell).recompile(true);
        }
    }


    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') {
                resolve();
            } else {
                window.addEventListener('load', () => {
                    resolve();
                });
            }
        });
    }


    private setReadOnly(ro : boolean) {
        this.querySelectorAll('tunepad-cell, tunepad-sample')?.forEach(cell => {
            cell.setAttribute('readonly', `${ro}`);
        });
        this.querySelector('tunepad-project-menu')?.setAttribute('readonly', `${ro}`);
    }


    private emitUpdateEvent(property : string, value : any) {
        const uuid = this.uuid;
        this.dispatchEvent(new CustomEvent("update-project", {
            bubbles: false,
            composed: true,
            detail: { uuid, property, value }
        }));
    }

    private emitLogEvent(event : string, data : any = { }) {
        this.dispatchEvent(new CustomEvent("project-log", {
            bubbles: false,
            composed: true,
            detail: { event, data }
        }));
    }

    private waitForElementConnected(element : Element) : Promise<boolean> {
        return new Promise(resolve => {
            function checkConnection() {
                if (element.isConnected) {
                    resolve(true);
                } else {
                    requestAnimationFrame(checkConnection);
                }
            }
            requestAnimationFrame(checkConnection);
        });
    }
}

